/**
 * useNotificationPermission — gating do convite de notificações (Story X1.2.2).
 *
 * Responsabilidades:
 *   1. Conta uma visita por carga do app (mount → recordVisit, 1×).
 *   2. Lê/persiste o dismiss de 30d no localStorage.
 *   3. Combina visitas + dismiss com os sinais externos (suporte, permissão,
 *      subscription — vindos de usePushSubscription) e decide `shouldPrompt`.
 *
 * Toda a decisão fica em lib/notificationPrompt.ts (pura). Aqui é só o wire com
 * localStorage + React state. O componente NotificationPrompt injeta os sinais
 * e chama subscribe() de fato.
 */
import { useCallback, useEffect, useState } from "react";

import {
  getVisitCount,
  isPromptDismissActive,
  recordVisit,
  setPromptDismissed,
  shouldShowPrompt,
  type NotifPermission,
} from "@/lib/notificationPrompt";

export interface UseNotificationPermissionInput {
  /** SW + Push API + Notification API disponíveis (de usePushSubscription). */
  isSupported: boolean;
  /** Estado atual de Notification.permission. */
  permission: NotifPermission;
  /** Já existe subscription ativa. */
  alreadySubscribed: boolean;
  /** O servidor tem chave VAPID? `undefined` = ainda checando. */
  serverHasVapid?: boolean;
  /** A intenção gravada neste aparelho é `ligado`? Então o convite não aparece (o aviso do push toma o lugar). */
  intencaoLigada?: boolean;
}

export interface UseNotificationPermissionState {
  /** Nº de visitas registradas (após o incremento deste mount). */
  visitCount: number;
  /** O convite deve ser exibido agora? */
  shouldPrompt: boolean;
  /** "Agora não": persiste o dismiss de 30d e oculta o convite. */
  dismiss: () => void;
}

/** Flag de sessão (aba) que dedupe a contagem de visita. */
const SESSION_VISIT_FLAG = "seweb:notif:visit-counted";

function getStore(): Storage | null {
  return typeof window !== "undefined" ? window.localStorage : null;
}

/** Lê do store com fail-safe: storage que lança (Safari private) → fallback. */
function safeRead<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

export function useNotificationPermission(
  input: UseNotificationPermissionInput
): UseNotificationPermissionState {
  const { isSupported, permission, alreadySubscribed, serverHasVapid, intencaoLigada } = input;
  const store = getStore();

  const [visitCount, setVisitCount] = useState<number>(() =>
    safeRead(() => (store ? getVisitCount(store) : 0), 0)
  );
  // Lido uma vez do storage no init; dismiss() apenas seta true (sem reler o
  // relógio a cada render — mantém o corpo do hook determinístico).
  const [dismissActive, setDismissActive] = useState<boolean>(() =>
    safeRead(() => (store ? isPromptDismissActive(store, Date.now()) : false), false)
  );

  // Conta no máximo UMA visita por sessão de aba: a flag em sessionStorage é
  // imune ao double-mount do StrictMode (dev) e a remounts do shell autenticado
  // dentro da mesma sessão. "Visita" = abrir o app numa aba nova/reload.
  useEffect(() => {
    if (!store) return;
    try {
      if (window.sessionStorage.getItem(SESSION_VISIT_FLAG)) return;
      window.sessionStorage.setItem(SESSION_VISIT_FLAG, "1");
      setVisitCount(recordVisit(store));
    } catch {
      // sessionStorage/localStorage indisponível (private mode/quota): não
      // conta a visita, mas também não derruba o app.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = useCallback(() => {
    // Oculta sempre na UI, mesmo se a persistência falhar (melhor que um
    // "Agora não" que não faz nada em Safari private mode).
    setDismissActive(true);
    const s = getStore();
    if (!s) return;
    try {
      setPromptDismissed(s, Date.now());
    } catch {
      // storage indisponível: o convite some nesta sessão, mas pode voltar
      // após reload — aceitável.
    }
  }, []);

  const shouldPrompt = shouldShowPrompt({
    isSupported,
    permission,
    visitCount,
    dismissActive,
    alreadySubscribed,
    serverHasVapid,
    intencaoLigada,
  });

  return { visitCount, shouldPrompt, dismiss };
}
